import { z } from "zod";
import { FilterOp, Identifier, type Capability, type ExposureContract } from "./exposure-contract";

/**
 * The universal typed QueryPlan. Natural language NEVER reaches a database;
 * it resolves to this structure, which is validated against the dataset's
 * approved capabilities and then compiled to parameterised SQL by the serving
 * engine using only identifiers from the contract.
 */
export const Scalar = z.union([z.string().max(512), z.number().finite(), z.boolean()]);
export const FilterValue = z.union([Scalar, z.array(Scalar).max(100)]);

export const QueryPlan = z.object({
  datasetId: z.string().max(40),
  capability: Identifier,
  /** field -> { op: value } ; plain value is shorthand for eq */
  filters: z.record(Identifier, z.union([Scalar, z.partialRecord(FilterOp, FilterValue)])).default({}),
  sort: z.array(z.object({ field: Identifier, dir: z.enum(["asc", "desc"]) })).max(4).default([]),
  limit: z.number().int().min(1).max(1000).default(25),
  cursor: z.string().max(256).optional(),
  /** Answer with the NUMBER of matching rows (search capabilities only): the same approved filters, no row data. */
  count: z.boolean().optional(),
});
export type QueryPlan = z.infer<typeof QueryPlan>;

export interface NormalisedPredicate { field: string; op: z.infer<typeof FilterOp>; value: unknown }
export interface ValidatedPlan {
  plan: QueryPlan;
  capability: Capability;
  predicates: NormalisedPredicate[];
  limit: number;
}

export class PlanValidationError extends Error {
  constructor(message: string, public readonly code = "invalid_plan") { super(message); }
}

/** Validate a plan against an approved contract. Rejects unknown capabilities/fields/ops rather than guessing. */
export function validatePlan(input: unknown, contract: Pick<ExposureContract, "datasetId" | "capabilities">): ValidatedPlan {
  const parsed = QueryPlan.safeParse(input);
  if (!parsed.success) throw new PlanValidationError(`malformed plan: ${parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; ")}`);
  const plan = parsed.data;
  if (plan.datasetId !== contract.datasetId) throw new PlanValidationError("plan targets a different dataset");
  const capability = contract.capabilities.find((c) => c.name === plan.capability);
  if (!capability) throw new PlanValidationError(`unsupported capability '${plan.capability}'`, "unsupported_capability");

  const predicates: NormalisedPredicate[] = [];
  for (const [field, spec] of Object.entries(plan.filters)) {
    const allowed = capability.filters.find((f) => f.field === field);
    if (!allowed) throw new PlanValidationError(`field '${field}' is not filterable on '${capability.name}'`, "field_not_allowed");
    const entries: [string, unknown][] = typeof spec === "object" && spec !== null && !Array.isArray(spec) ? Object.entries(spec) : [["eq", spec]];
    for (const [op, value] of entries) {
      const o = FilterOp.parse(op);
      if (!allowed.ops.includes(o)) throw new PlanValidationError(`operator '${o}' not allowed on '${field}'`, "operator_not_allowed");
      if (o === "in" ? !Array.isArray(value) : Array.isArray(value)) throw new PlanValidationError(`bad value shape for ${field}.${o}`);
      predicates.push({ field, op: o, value });
    }
  }
  for (const req of capability.filters.filter((f) => f.required)) {
    if (!predicates.some((p) => p.field === req.field)) throw new PlanValidationError(`filter '${req.field}' is required`, "missing_required_filter");
  }
  for (const s of plan.sort) {
    const allowed = capability.sort.find((x) => x.field === s.field);
    if (!allowed || !allowed.dirs.includes(s.dir)) throw new PlanValidationError(`sort ${s.field} ${s.dir} not allowed`, "sort_not_allowed");
  }
  if (plan.count && capability.kind !== "search") throw new PlanValidationError(`'${capability.name}' cannot be counted`, "count_not_allowed");
  predicates.sort((a, b) => (a.field + a.op).localeCompare(b.field + b.op));
  return { plan, capability, predicates, limit: Math.min(plan.limit, capability.maxRows) };
}
